import type { SharedQuestion } from '@ralph/contracts'
import clsx from 'clsx'
import { TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { Badge, EmptyState, Spinner } from '@/components/ui/primitives.tsx'
import { useFeedbackSummary, useOwnerAnalytics } from '@/hooks/use-engagement.ts'
import { useAgentKnowledgeUsage } from '@/hooks/use-knowledge-bases.ts'
import { describeIssue, useI18n } from '@/i18n/index.ts'
import {
  feedbackTotals,
  formatRate,
  sortHealthEvents,
  sortSharedQuestions,
  versionFeedbackRows
} from './analytics-presentation.ts'
import { formatSpace } from './knowledge-coverage.ts'
import { KNOWLEDGE_WINDOW_DAYS } from './maker-limits.ts'

/**
 * What an owner can see about their own agent (AGM-120).
 *
 * The panel is built out of exactly what the two owner-facing contracts carry, and the
 * omissions are the design: no conversation list, no rater identity, no cost. An owner
 * improves an agent from counts, rates and the questions people deliberately handed them —
 * anything more would make publishing an agent a way to read colleagues' chats.
 *
 * Rendered only when opened, and both reads are gated on that: "Your agents" can list ten
 * agents, and ten aggregate queries to draw ten collapsed panels is a page that loads slowly
 * to show nothing.
 */
export const InsightsPanel = ({ agentId, open }: { agentId: string; open: boolean }) => {
  // Kept whole rather than destructured: the pure presentation modules below take the reader's
  // language as a parameter, and threading one object is how they get it without any of them
  // capturing a locale at module scope (PRD-27 F-3). The words arrive the same way — the panel
  // used to read `insightsStrings` at import, which is the same capture one level up.
  const i18n = useI18n()
  const t = i18n.t.marketplace.maker.insights
  const analytics = useOwnerAnalytics(agentId, open)
  const feedback = useFeedbackSummary(agentId, open)

  if (!open) return null

  if (analytics.isLoading) {
    return (
      <p className="readout mt-3 flex items-center gap-2 text-fg-subtle">
        <Spinner size={12} />
        {t.loading}
      </p>
    )
  }

  if (analytics.isError || !analytics.data) {
    return (
      <div className="mt-3">
        <EmptyState title={t.unavailable} body={t.unavailableBody} />
      </div>
    )
  }

  const data = analytics.data
  const totals = feedbackTotals(i18n, data.feedback)
  const zeroHit = formatRate(i18n, data.zeroHitRate)
  const abstention = formatRate(i18n, data.abstentionRate)
  const versions = versionFeedbackRows(feedback.data)
  const questions = sortSharedQuestions(data.sharedQuestions)
  const events = sortHealthEvents(data.healthEvents)

  return (
    <div className="mt-3.5 border-t border-rule pt-3.5">
      <h3 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.title}</h3>
      <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{t.lead}</p>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        <Figure label={t.conversations} value={String(data.conversations)} />
        <Figure label={t.people} value={String(data.uniqueUsers)} />
        <Figure
          label={t.helpful}
          value={String(totals.helpful)}
          hint={totals.helpfulShare === null ? t.noRatings : t.helpfulShare(totals.helpfulShare)}
        />
        <Figure label={t.unhelpful} value={String(totals.unhelpful)} />
        <Figure
          label={t.zeroHitTitle}
          value={zeroHit ?? t.notMeasurable}
          hint={t.zeroHitHint}
          muted={zeroHit === null}
        />
        <Figure
          label={t.abstentionTitle}
          value={abstention ?? t.notMeasurable}
          hint={t.abstentionHint}
          muted={abstention === null}
        />
      </div>

      <Block title={t.versionsTitle} hint={t.versionsHint}>
        {versions.length === 0 ? (
          <p className="text-[13px] text-fg-subtle">{t.versionsEmpty}</p>
        ) : (
          <ul>
            {versions.map((row) => (
              <li
                key={row.definitionVersion ?? 'unversioned'}
                className="flex flex-wrap items-baseline justify-between gap-2 border-b border-rule py-1.5 text-[13px] last:border-0"
              >
                <span>{row.definitionVersion === null ? t.versionUnknown : t.versionLabel(row.definitionVersion)}</span>
                <span className="readout text-fg-muted">{t.ratingsLine(row.helpful, row.unhelpful)}</span>
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block title={t.notesTitle} hint={t.notesHint}>
        {(feedback.data?.recentNotes.length ?? 0) === 0 ? (
          <p className="text-[13px] text-fg-subtle">{t.notesEmpty}</p>
        ) : (
          <ul className="space-y-2">
            {feedback.data?.recentNotes.map((note) => (
              <li key={`${note.createdAt}-${note.note.slice(0, 24)}`}>
                <div className="card rounded-tile px-3 py-2.5">
                  <p className="text-pretty text-[13px] leading-[1.5]">{note.note}</p>
                  <p className="readout mt-1.5 text-fg-subtle">
                    {note.helpful ? t.helpful : t.unhelpful} · {i18n.formatAgo(note.createdAt)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Block>

      <Block title={t.questionsTitle} hint={t.questionsHint}>
        {questions.length === 0 ? (
          <p className="text-[13px] text-fg-subtle">{t.questionsEmpty}</p>
        ) : (
          <ul className="space-y-2">
            {questions.map((question) => (
              <SharedQuestionRow key={question.id} question={question} />
            ))}
          </ul>
        )}
      </Block>

      <KnowledgeBlock agentId={agentId} open={open} />

      <Block title={t.healthTitle}>
        {events.length === 0 ? (
          <p className="text-[13px] text-fg-subtle">{t.healthEmpty}</p>
        ) : (
          <ul className="space-y-1.5">
            {events.map((event) => (
              <li key={`${event.at}-${event.code}`} className="flex items-start gap-2 text-[13px] leading-snug">
                {/* Icon plus text, never colour alone — the health signal has to survive a
                    monochrome screen and colour-blind reading (memo: web design language). */}
                <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0 text-alert" aria-hidden />
                <span className="min-w-0">
                  {/* The same record the health panel renders, through the same catalogue
                      entry, so one finding cannot read two ways on two of this owner's
                      screens. The stored English is the fallback for a snapshot older than
                      its key (PRD-27 D-4, LOC-033). */}
                  {describeIssue(i18n.t, event) ?? event.message}
                  <span className="readout ml-1.5 text-fg-subtle">{i18n.formatAgo(event.at)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Block>
    </div>
  )
}

/**
 * Which of the owner's material is actually answering questions (AGM-120).
 *
 * The one thing an owner could not previously see about their own agent. Coverage in the builder
 * already answers "can the audience read this base"; nothing answered "does any answer ever come
 * from it" — so a maker could attach five bases, share every one of them correctly, and never
 * learn that four of them had never been the source of anything.
 *
 * Three counts, and each of them is recorded rather than derived: how often the agent looked
 * something up, how often that came back empty, and how many answers were built on a passage it
 * found. No rate, no score, no money.
 *
 * It disappears entirely when the read fails rather than showing a failure: this is an addition
 * to a panel whose other four blocks work, and one unavailable aggregate must not make the whole
 * insights view look broken. The `open` gate keeps ten collapsed panels from making ten requests.
 */
const KnowledgeBlock = ({ agentId, open }: { agentId: string; open: boolean }) => {
  const i18n = useI18n()
  const t = i18n.t.marketplace.maker.insights
  const usage = useAgentKnowledgeUsage(agentId, KNOWLEDGE_WINDOW_DAYS, open)

  if (usage.isError) return null

  if (usage.isLoading || !usage.data) {
    return (
      <Block title={t.knowledgeTitle}>
        <p className="readout flex items-center gap-2 text-fg-subtle">
          <Spinner size={12} />
          {t.loading}
        </p>
      </Block>
    )
  }

  const data = usage.data

  return (
    <Block title={t.knowledgeTitle} hint={t.knowledgeHint}>
      <div className="mb-2.5 grid gap-2 sm:grid-cols-3">
        <Figure label={t.knowledgeSearches} value={String(data.searches)} />
        <Figure label={t.knowledgeFoundNothing} value={String(data.zeroHitSearches)} />
        {/* The "N of M" hint is dropped when nothing has been answered: "0 of 0 answers" is a
            ratio of nothing, and it reads as a fault rather than as an agent nobody has used. */}
        <Figure
          label={t.knowledgeGrounded}
          value={String(data.groundedAnswers)}
          {...(data.answers > 0 ? { hint: t.knowledgeGroundedHint(i18n, data.groundedAnswers, data.answers) } : {})}
        />
      </div>

      {data.bases.length === 0 ? (
        <p className="text-[13px] leading-[1.5] text-fg-subtle">{t.knowledgeEmpty}</p>
      ) : (
        <>
          <ul>
            {data.bases.map((base) => (
              <li
                key={base.baseId}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-rule py-2 last:border-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px]">{base.name}</span>
                  <span className="readout mt-0.5 block text-fg-subtle">
                    {t.baseHolding(
                      i18n,
                      base.documentCount,
                      formatSpace(i18n.t.marketplace.maker, i18n, base.storageBytes)
                    )}
                    {base.mine ? '' : ` · ${t.baseOwnedBy(base.ownerName)}`}
                  </span>
                </span>
                {/* The state is a word first and a colour second — never colour alone (AGM-124). */}
                {base.answersUsing > 0 ? (
                  <span className="readout shrink-0 text-grounded">
                    {t.baseUsed(i18n, base.answersUsing)}
                    {base.lastUsedAt ? ` · ${t.baseLastUsed(i18n.formatAgo(base.lastUsedAt))}` : ''}
                  </span>
                ) : (
                  <Badge>{t.baseUnused}</Badge>
                )}
              </li>
            ))}
          </ul>

          {data.bases.some((base) => base.answersUsing === 0) && (
            <p className="mt-2 text-pretty text-[12.5px] leading-[1.45] text-fg-subtle">{t.baseUnusedHint}</p>
          )}
          <p className="readout mt-1.5 text-fg-subtle">{t.knowledgeWindow(i18n, data.windowDays)}</p>
        </>
      )}
    </Block>
  )
}

/** One titled block, opened by an eyebrow with the hairline running off it. */
const Block = ({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) => (
  <section className="mt-4">
    <div className="flex items-center gap-2.5 pb-2">
      <h4 className="eyebrow">{title}</h4>
      <span aria-hidden className="h-px flex-1 bg-rule" />
    </div>
    {hint && <p className="mb-2 text-pretty text-[12.5px] leading-[1.5] text-fg-subtle">{hint}</p>}
    {children}
  </section>
)

/**
 * One shared question.
 *
 * Read-only, and deliberately so: the platform has no endpoint that resolves one, so a
 * "Mark handled" button here would be a control that does nothing. The status still renders
 * because the contract carries it — when the resolve route lands, the button belongs here.
 */
const SharedQuestionRow = ({ question }: { question: SharedQuestion }) => {
  const { formatAgo, t: messages } = useI18n()
  const t = messages.marketplace.maker.insights
  return (
    <li>
      <div className="card rounded-tile px-3 py-2.5">
        <p className="text-pretty text-[13px] leading-[1.5]">{question.question}</p>
        <div className="mt-1.5 flex items-center gap-2">
          <Badge tone={question.status === 'open' ? 'primary' : 'neutral'}>
            {question.status === 'open' ? t.questionOpen : t.questionResolved}
          </Badge>
          <span className="readout text-fg-subtle">{formatAgo(question.createdAt)}</span>
        </div>
      </div>
    </li>
  )
}

/**
 * A measured figure.
 *
 * The value is set in tabular mono because every measured number in this product is, and
 * because these tiles sit in a grid where the digits should line up column to column. `muted`
 * is the "we cannot measure this" case: it is a sentence, not a number, so it drops out of
 * the mono grammar rather than pretending to be a reading.
 */
const Figure = ({ label, value, hint, muted }: { label: string; value: string; hint?: string; muted?: boolean }) => (
  <div className="rounded-tile border border-rule bg-card-solid px-3 py-2.5">
    <p className="eyebrow">{label}</p>
    <p
      className={clsx(
        'mt-1',
        muted ? 'text-[12.5px] leading-[1.4] text-fg-subtle' : 'readout text-[19px] font-medium tracking-[-0.02em]'
      )}
    >
      {value}
    </p>
    {hint && <p className="mt-1 text-pretty text-[12px] leading-[1.4] text-fg-subtle">{hint}</p>}
  </div>
)
